import * as React from "react";
import {inject, observer} from "mobx-react";
import styled from "styled-components";
import type {Field as FieldModel} from "../../../../../data/models/field/Field";
import {FieldDataTypes} from "../../../../../data/models/field/FieldDataTypes";
import type {ISwitchListChange} from "../../../../widgets/button/switch/SwitchList";
import type {AppState} from "../../../../../data/state/AppState";
import {XyiconFeature, FieldDataType} from "../../../../../generated/api/base";
import {ArrayUtils} from "../../../../../utils/data/array/ArrayUtils";
import type {TransportLayer} from "../../../../../data/TransportLayer";
import type {ISelectSliderOption} from "../../../../widgets/input/selectslider/SelectSlider";
import {featureTitles} from "../../../../../data/state/AppStateConstants";
import {ClickToEditInputStyled, ClickToEditInputV5} from "../../../input/clicktoedit/ClickToEditInputV5";
import {SelectInputV5} from "../../../input/select/SelectInputV5";
import {ToggleSwitchFieldV5} from "../../../details/ToggleSwitchFieldV5";
import {FieldV5} from "../../../details/FieldV5";
import {ToggleContainerV5} from "../../../widgets/ToggleContainerV5/ToggleContainerV5";
import {SelectSliderV5} from "../../../details/SelectSliderV5";
import {SingleLineLabelV5} from "../../../details/datatypes/SingleLineLabelV5";
import {PopupUtilsV5} from "../../../popup/PopupUtilsV5";
import HelpIcon from "../../../../../ui/5.0/icons/circle-question.svg?react";
import {FieldDetailsContentSettingsV5} from "../../../../modules/settings/modules/field/FieldDetailsContentConstants";
import {colorPalette} from "../../../styles/colorPalette";
import {baseDistance} from "../../../styles/styles";
import {FieldStyled} from "../../../details/Field.styled";
import {MultiLineLabelStyled} from "../../../details/datatypes/multi/MultiLineInputV5";
import {SingleLineLabelStyled} from "../../../details/datatypes/SingleLineInputV5";
import {TextInputV5} from "../../../details/datatypes/TextInputV5";

interface IFieldFormProps {
	readonly field: FieldModel;
	readonly typesFeature: XyiconFeature;
	readonly createPanelNameInput?: (name: string) => void;
	readonly appState?: AppState;
	readonly transport?: TransportLayer;
}

interface IFieldFormState {
	isMappingInProgress: boolean;
}

enum AssignType {
	Unassigned = 0,
	Assigned = 1,
	Lookup = 2,
}

@inject("appState")
@inject("transport")
@observer
export class FieldFormV5 extends React.Component<IFieldFormProps, IFieldFormState> {
	private _updateFieldTimeoutId: number = null;
	private _updateFieldFormulaTimeoutId: number = null;
	private _lastValidFieldName = this.props.field.name;
	public _assignedTypes: ISelectSliderOption[] = [
		{
			id: AssignType.Unassigned,
			label: AssignType[AssignType.Unassigned],
		},
		{
			id: AssignType.Assigned,
			label: AssignType[AssignType.Assigned],
		},
		this.props.field.feature === XyiconFeature.Xyicon && {
			id: AssignType.Lookup,
			label: "Lookup Link",
			tooltip: "Lookup Link is only applicable for Text (Single Line) fields where Display On Links is enabled.",
		},
	].filter((type) => type);

	constructor(props: IFieldFormProps) {
		super(props);

		this.state = {
			isMappingInProgress: false,
		};
	}

	private onMappingChange = (changes: ISwitchListChange[]) => {
		const {field, appState} = this.props;
		const fieldId = field.id;
		const mapping = appState.typeFieldMapping[field.feature];

		for (const change of changes) {
			const typeId = change.id;

			if (!mapping[typeId]) {
				mapping[typeId] = new Set();
			}

			const mappingForType = mapping[typeId];

			if (change.value) {
				mappingForType.add(fieldId);
			} else {
				mappingForType.delete(fieldId);
			}

			this.updateMapping(typeId);
		}
	};

	private onSliderMappingChange = async (labels: string[], value: AssignType) => {
		this.setState({isMappingInProgress: true});

		try {
			const {field, typesFeature, appState} = this.props;
			const typeList = appState.actions.getTypesForField(field, typesFeature);
			const mapping = appState.typeFieldMapping[field.feature];
			const updatedTypeIds: string[] = [];

			labels.forEach((label) => {
				const typeItem = typeList.find((type) => type.label === label);
				const typeObj = appState.actions.getTypeById(typeItem.id);
				if (!typeItem || !typeObj) {
					return;
				}

				if (!mapping[typeObj.id]) {
					mapping[typeObj.id] = new Set();
				}
				const mappingForType = mapping[typeObj.id];

				if (value === AssignType.Assigned) {
					mappingForType.add(field.id);
				} else {
					mappingForType.delete(field.id);
				}

				typeObj.settings.xyiconLookupLinkFieldList ||= [];

				if (value === AssignType.Lookup) {
					typeItem.value = false;
					ArrayUtils.addMutable(typeObj.settings.xyiconLookupLinkFieldList, field.id);
				} else {
					typeItem.value = value === AssignType.Assigned;
					ArrayUtils.removeMutable(typeObj.settings.xyiconLookupLinkFieldList, field.id);
				}

				updatedTypeIds.push(typeObj.id);
			});

			const assignedTypeIds: string[] = Object.entries(mapping)
				.filter(([_, fieldSet]) => fieldSet.has(field.id))
				.map(([typeId]) => typeId);

			if (field.feature === XyiconFeature.Xyicon) {
				await this.props.transport.services.typefield.updateLookup(field.feature, field.id, updatedTypeIds, value === AssignType.Lookup);
			}

			await this.props.transport.services.typefield.applyFieldMapping(field.feature, field.id, assignedTypeIds);

			// Trigger re-evaluation of spotlight rules for field-based rules
			// The local typeFieldMapping is already updated above (lines 124-128), so we just need to trigger re-evaluation
			// This ensures colors are removed when fields are unassigned from types
			const spaceViewRenderer = appState.app.spaceViewRenderer;
			if (spaceViewRenderer?.isMounted) {
				const {xyiconManager, boundaryManager} = spaceViewRenderer;
				xyiconManager?.onFormattingRulesModified();
				boundaryManager?.onFormattingRulesModified();
			}
		} finally {
			this.setState({isMappingInProgress: false});
		}
	};

	private updateMapping(typeId: string) {
		const selected = this.props.field;

		if (selected) {
			const services = this.props.transport.services;

			services.typefield.applyTypeMapping(selected.feature, typeId);
		}
	}

	private onNameChange = () => {
		if (this.isFieldNameValid(this.props.field.name)) {
			this.updateField();
		} else {
			this.props.field.name = this._lastValidFieldName;
		}
	};

	private isFieldNameValid = (name: string) => {
		const {field, appState} = this.props;

		return appState.actions.isFieldNameValid(name, field);
	};

	private getErrorMessage = (name: string) => {
		return !name.trim() ? "Name cannot be empty!" : this.isFieldNameValid(name) ? "" : "Name needs to be unique!";
	};

	private onChangeDisplayOnLinks = (value: boolean) => {
		const {field, typesFeature, appState} = this.props;
		const list = appState.actions.getTypesForField(field, typesFeature);

		field.displayOnLinks = value;

		if (!value && field.feature === XyiconFeature.Xyicon) {
			const savedList = JSON.stringify(list);

			list.forEach((item) => {
				const type = appState.actions.getTypeById(item.id);
				const savedTypeObjStr = JSON.stringify({data: type.data, settings: type.settings});

				type.settings.xyiconLookupLinkFieldList = type.settings.xyiconLookupLinkFieldList || [];

				if (type.settings.xyiconLookupLinkFieldList.includes(field.id)) {
					item.value = false;
				}

				ArrayUtils.removeMutable(type.settings.xyiconLookupLinkFieldList, field.id);

				if (savedTypeObjStr !== JSON.stringify({data: type.data, settings: type.settings})) {
					this.props.transport.services.typefield.updateType(type);
				}
			});

			if (savedList !== JSON.stringify(list)) {
				this.onMappingChange(list);
			}
		}

		this.updateField();
	};

	private updateField() {
		clearTimeout(this._updateFieldTimeoutId);
		this._updateFieldTimeoutId = window.setTimeout(() => this.saveFieldChanges(), 500);
	}

	private updateFieldFormula() {
		clearTimeout(this._updateFieldFormulaTimeoutId);
		this._updateFieldFormulaTimeoutId = window.setTimeout(() => this.saveFieldFormulaChanges(), 500);
	}

	private saveFieldChanges = async () => {
		const {field, transport} = this.props;

		if (field.refId) {
			const {error} = await transport.services.typefield.updateField(field);

			if (error) {
				console.warn(error);
			} else {
				this._lastValidFieldName = field.name;
			}
		}
	};

	private saveFieldFormulaChanges = async () => {
		const {field, transport} = this.props;

		if (field.refId) {
			const {error} = await transport.services.typefield.updateFieldFormula(field);

			if (error) {
				console.warn(error);
			}
		}
	};

	private onChangeSettings = () => {
		this.updateField();
	};

	private getDataTypeSettingsComponent() {
		const {field} = this.props;

		const SettingsComponent = FieldDetailsContentSettingsV5[field.dataType];

		if (SettingsComponent) {
			return (
				<SettingsComponent
					field={field}
					onChange={this.onChangeSettings}
				/>
			);
		}
		return false;
	}

	private toggleHasFormula = async (value: boolean) => {
		const {field} = this.props;

		field.hasFormula = value;

		if (!value) {
			const confirmed = await PopupUtilsV5.getFormulaDeleteConfirmationPopupV5(field.feature);

			if (confirmed) {
				field.formula = "";
				this.updateFieldFormula();
			}
		}
	};

	private onFormulaChange = async (value: string) => {
		const {field} = this.props;
		const confirmed = await PopupUtilsV5.getFormulaChangeConfirmationPopupV5(field.feature);

		if (confirmed) {
			field.formula = value;
			this.updateFieldFormula();
		}
	};

	private onFieldDescriptionChange = (value: string) => {
		const {field} = this.props;

		field.fieldDescription = value;
	};

	private toggleAssignByModel = (value: boolean) => {
		const {field} = this.props;

		field.isAssignedByModel = value;

		this.saveFieldChanges();
	};

	private onHelperTextChange = (value: string) => {
		const {field} = this.props;

		field.helperText = value;
	};

	public override componentDidMount() {
		const {field, typesFeature, appState} = this.props;
		const list = appState.actions.getTypesForField(field, typesFeature);

		list.forEach((item) => {
			const type = appState.actions.getTypeById(item.id);

			if (!type.settings.xyiconLookupLinkFieldList) {
				type.settings.xyiconLookupLinkFieldList = [];
			}
		});
	}

	public override componentWillUnmount() {
		clearTimeout(this._updateFieldTimeoutId);
		clearTimeout(this._updateFieldFormulaTimeoutId);
	}

	public override render() {
		const {field, typesFeature, appState, createPanelNameInput} = this.props;
		const {isMappingInProgress} = this.state;
		const editMode = !!field.refId;
		const fieldDataType = FieldDataTypes.map[field.dataType];
		const list = appState.actions.getTypesForField(field, typesFeature);
		const selectableTypes = FieldDataTypes.array
			.filter((config) => !config.system)
			.filter((config) => ![FieldDataType.PeopleSingleChoiceList, FieldDataType.PeopleMultipleChoiceList].includes(config.id));

		const isPeopleMultiChoiceSelected = field.dataType === FieldDataType.PeopleMultipleChoiceList;

		// Show only one "People" field in the dropdown
		// The variant (single/multiple) will be determined based on the "Selection Mode" dropdown in PeopleFieldSettingsV5
		if (isPeopleMultiChoiceSelected) {
			selectableTypes.push(FieldDataTypes.map[FieldDataType.PeopleMultipleChoiceList]);
		} else {
			selectableTypes.push(FieldDataTypes.map[FieldDataType.PeopleSingleChoiceList]);
		}

		const isExistingField = !!field.refId;

		return (
			<FieldFormContainerStyled>
				<FieldFormHeaderContainerStyled>
					{editMode && (
						<FieldV5 label="ID">
							<SingleLineLabelV5 value={field.refId} />
						</FieldV5>
					)}
					<FieldV5
						className={!editMode && "focused"}
						label="Name"
					>
						<TextInputV5
							value={field.name}
							updateLastValidOnInput={isExistingField}
							onChange={(value) => (field.name = value)}
							onBlur={this.onNameChange}
							getErrorMessage={this.getErrorMessage}
							autoFocus={!!createPanelNameInput}
						/>
					</FieldV5>
					<FieldV5
						label="Type"
						className={!editMode && "focused"}
					>
						{editMode ? (
							<SingleLineLabelV5 value={fieldDataType.title} />
						) : (
							<SelectInputV5
								options={selectableTypes}
								renderOptionWhenClosed={(type) => type.title}
								selected={fieldDataType}
								onChange={(type) => (field.dataType = type.id)}
								isSameWidth={true}
								// searchBar={false}
							/>
						)}
					</FieldV5>
					{this.getDataTypeSettingsComponent()}
					<FieldV5 label="Display on Links">
						<ToggleSwitchFieldV5
							label=""
							value={field.displayOnLinks}
							onChange={this.onChangeDisplayOnLinks}
							noBooleanLabel
						/>
					</FieldV5>
					{[XyiconFeature.Xyicon, XyiconFeature.XyiconCatalog].includes(field.feature) && (
						<FieldV5
							label="Assign by Model"
							icons={{postLabelIcon: HelpIcon}}
							tooltips={{
								postLabelIconTooltip: `Turning this toggle on allows this ${featureTitles[field.feature].toLowerCase()} field to be assigned to the Catalog Model.`,
							}}
						>
							<ToggleSwitchFieldV5
								value={field.isAssignedByModel}
								onChange={this.toggleAssignByModel}
								label=""
								noBooleanLabel
							/>
						</FieldV5>
					)}
					{[XyiconFeature.Boundary, XyiconFeature.Xyicon].includes(field.feature) &&
						![FieldDataType.PeopleSingleChoiceList, FieldDataType.PeopleMultipleChoiceList].includes(field.dataType) && (
							<>
								<FieldV5 label="Has Formula">
									<ToggleSwitchFieldV5
										value={field.hasFormula}
										onChange={this.toggleHasFormula}
										label=""
										noBooleanLabel
									/>
								</FieldV5>
								{field.hasFormula && (
									<FieldV5 label="Formula">
										<ClickToEditInputV5
											dataType={FieldDataType.MultiLineText}
											onChange={this.onFormulaChange}
											value={field.formula || undefined}
										/>
									</FieldV5>
								)}
							</>
						)}
					{field.hasValidation && (
						<FieldV5 label="Validation Rule">
							<ClickToEditInputV5
								dataType={FieldDataType.SingleLineText}
								value={field.validation}
								disabled={true}
							/>
						</FieldV5>
					)}
					<FieldV5
						label="Field Description"
						icons={{postLabelIcon: HelpIcon}}
						tooltips={{
							postLabelIconTooltip: "A detailed description explaining the intended use of the field. The description is only visible in Settings.",
						}}
						className="higher"
					>
						<ClickToEditInputV5
							value={field.fieldDescription}
							onLiveChange={this.onFieldDescriptionChange}
							onBlur={() => this.updateField()}
							dataType={FieldDataType.MultiLineText}
						/>
					</FieldV5>
					<FieldV5
						label="Helper Text"
						icons={{postLabelIcon: HelpIcon}}
						tooltips={{
							postLabelIconTooltip:
								"A short explanation designed to help the user understand the field's purpose. The text is displayed when the user hovers over the field's label on the Details tab.",
						}}
						className="higher"
					>
						<ClickToEditInputV5
							value={field.helperText}
							onLiveChange={this.onHelperTextChange}
							onBlur={() => this.updateField()}
							dataType={FieldDataType.MultiLineText}
						/>
					</FieldV5>
				</FieldFormHeaderContainerStyled>
				{field.id && (
					<ToggleContainerV5
						title="Assigned Types"
						open={true}
					>
						<SelectSliderV5
							options={this._assignedTypes}
							rows={list.map((t) => {
								let value = t.value ? AssignType.Assigned : AssignType.Unassigned;
								const typedObj = appState.actions.getTypeById(t.id);
								let disabledOptionsList: ISelectSliderOption[] = [];

								if (field.feature === XyiconFeature.Xyicon) {
									if (!field?.displayOnLinks || field.dataType !== FieldDataType.SingleLineText) {
										disabledOptionsList.push({id: AssignType.Lookup, label: this._assignedTypes.find((at) => at.id === AssignType.Lookup).label});
									}

									if (typedObj.settings.xyiconLookupLinkFieldList?.includes(field.id)) {
										value = AssignType.Lookup;
									}
								}

								return {
									label: t.label,
									value,
									disabledOptionsList,
								};
							})}
							onChange={this.onSliderMappingChange}
							disabled={isMappingInProgress}
						/>
					</ToggleContainerV5>
				)}
			</FieldFormContainerStyled>
		);
	}
}

const FieldFormContainerStyled = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${baseDistance.md};
	position: relative;
	z-index: 1;
`;

const FieldFormHeaderContainerStyled = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${baseDistance.md};
	padding-bottom: ${baseDistance.md};
	border-bottom: 2px solid ${colorPalette.gray.c300};

	${FieldStyled} {
		.label {
			display: flex;
			justify-content: space-between;
			padding-right: ${baseDistance.sm};

			svg {
				width: 18px;
				height: 18px;
				color: ${colorPalette.primary.c500Primary};
			}
		}

		${ClickToEditInputStyled} ${MultiLineLabelStyled}, ${ClickToEditInputStyled} ${SingleLineLabelStyled} {
			border: 1px solid ${colorPalette.gray.c300};
			border-radius: ${baseDistance.xs};
			cursor: text;
			min-height: 32px;
		}
	}
`;
